import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import { Loader2, ShoppingBag, ExternalLink, Check } from 'lucide-react';

const ClientRecommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    fetchRecommendations();
  }, [clientId, navigate]);

  const fetchRecommendations = async () => {
    try {
      const data = await clientAPI.getRecommendations(clientId);
      setRecommendations(data);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientLayout>
      <div style={{ maxWidth: '1000px' }} data-testid="recommendations-page">
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: 300,
            marginBottom: 'var(--space-s)'
          }}>
            Рекомендации к покупке
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--mid-gray)',
            fontSize: '0.875rem'
          }}>
            Вещи, подобранные вашим стилистом
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px'
          }}>
            <Loader2 
              size={32} 
              style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} 
            />
          </div>
        ) : recommendations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-xxxl)',
            backgroundColor: 'var(--white)',
            border: '1px solid var(--soft-gray)'
          }}>
            <ShoppingBag size={48} style={{ color: 'var(--light-gray)', marginBottom: 'var(--space-l)' }} />
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              marginBottom: 'var(--space-m)'
            }}>
              Пока нет рекомендаций
            </p>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              Здесь появятся рекомендации к покупкам от вашего стилиста — 
              вещи, которые идеально дополнят ваш гардероб.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--space-l)'
          }}
          data-testid="recommendations-grid"
          >
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                data-testid={`recommendation-card-${rec.id}`}
                style={{
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)',
                  overflow: 'hidden',
                  transition: 'all 300ms ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Purchased Badge */}
                {rec.is_purchased && (
                  <div style={{
                    position: 'absolute',
                    top: 'var(--space-m)',
                    right: 'var(--space-m)',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    padding: '4px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.75rem',
                    fontFamily: 'var(--font-body)',
                    zIndex: 1
                  }}>
                    <Check size={12} />
                    Куплено
                  </div>
                )}

                {/* Image */}
                {rec.image_url && (
                  <div style={{
                    height: '240px',
                    overflow: 'hidden',
                    backgroundColor: 'var(--cream)'
                  }}>
                    <img
                      src={rec.image_url}
                      alt={rec.description}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: rec.is_purchased ? 0.7 : 1
                      }}
                    />
                  </div>
                )}

                <div style={{ padding: 'var(--space-l)' }}>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.9375rem',
                    color: 'var(--charcoal)',
                    lineHeight: 1.6,
                    marginBottom: 'var(--space-l)'
                  }}>
                    {rec.description}
                  </p>

                  <a
                    href={rec.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-s)',
                      padding: '12px 20px',
                      backgroundColor: rec.is_purchased ? 'var(--soft-gray)' : 'var(--black)',
                      color: rec.is_purchased ? 'var(--mid-gray)' : 'var(--white)',
                      textDecoration: 'none',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.8125rem',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      transition: 'all 200ms ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!rec.is_purchased) {
                        e.currentTarget.style.backgroundColor = 'var(--charcoal)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!rec.is_purchased) {
                        e.currentTarget.style.backgroundColor = 'var(--black)';
                      }
                    }}
                  >
                    <ExternalLink size={14} />
                    {rec.is_purchased ? 'Посмотреть' : 'Перейти к покупке'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </ClientLayout>
  );
};

export default ClientRecommendations;
